import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Row, Col, Card, CardBody, Button, Spinner, Alert, Modal, ModalHeader, ModalBody, ModalFooter, Badge } from "reactstrap";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ApiClass from "../apiManager/apiClass";
import { PID_TO_DISPLAY_NAME } from "../data/products";
import "./DashboardPage.css";

const HOSTING_NOODLE_PIDS = Object.keys(PID_TO_DISPLAY_NAME).map(Number);
const CONNECTABLE_PIDS = new Set([4, 5, 6, 7]);

const DashboardPage = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionMsg, setActionMsg] = useState("");
  const [manageOpen, setManageOpen] = useState(false);
  const [manageLoading, setManageLoading] = useState(false);
  const [manageError, setManageError] = useState("");
  const [manageService, setManageService] = useState(null);
  const [subDetails, setSubDetails] = useState(null);
  const navigate = useNavigate();
  const apiClass = ApiClass.getInstance();

  useEffect(() => {
    const load = async () => {
      const token = localStorage.getItem("hosting-noodle-token");
      if (!token) {
        navigate("/login");
        return;
      }
      setLoading(true);
      setError("");
      setActionMsg("");
      try {
        const res = await apiClass.listStripeSubscriptions();
        const subs = res?.responseData?.msg || res?.msg || [];
        const hostingNoodleSubs = subs.filter((s) =>
          HOSTING_NOODLE_PIDS.includes(parseInt(s.productID, 10))
        );
        const mapped = hostingNoodleSubs.map((s) => ({
          ...s,
          displayName: PID_TO_DISPLAY_NAME[s.productID] || `Product ${s.productID}`,
        }));
        setServices(mapped);
      } catch (err) {
        setError(err?.message || err?.msg || "Failed to load services.");
        setServices([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [apiClass, navigate]);

  const reloadServices = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await apiClass.listStripeSubscriptions();
      const subs = res?.responseData?.msg || res?.msg || [];
      const hostingNoodleSubs = subs.filter((s) =>
        HOSTING_NOODLE_PIDS.includes(parseInt(s.productID, 10))
      );
      const mapped = hostingNoodleSubs.map((s) => ({
        ...s,
        displayName: PID_TO_DISPLAY_NAME[s.productID] || `Product ${s.productID}`,
      }));
      setServices(mapped);
    } catch (err) {
      setError(err?.message || err?.msg || "Failed to load services.");
      setServices([]);
    } finally {
      setLoading(false);
    }
  };

  const loadSubscriptionDetails = async (subID) => {
    setManageLoading(true);
    setManageError("");
    try {
      const res = await apiClass.getStripeSubscriptionDetails(subID);
      const msg = res?.msg ?? res?.responseData?.msg;
      setSubDetails(msg || null);
    } catch (err) {
      setManageError(err?.message || err?.msg || "Failed to load subscription details.");
      setSubDetails(null);
    } finally {
      setManageLoading(false);
    }
  };

  const openManage = async (service) => {
    setManageService(service);
    setManageOpen(true);
    await loadSubscriptionDetails(service?.subID);
  };

  const closeManage = () => {
    setManageOpen(false);
    setManageService(null);
    setSubDetails(null);
    setManageError("");
  };

  const handlePause = async () => {
    setManageError("");
    try {
      const res = await apiClass.pauseStripeSubscription(manageService?.subID);
      setActionMsg(res?.msg || "Subscription paused.");
      await loadSubscriptionDetails(manageService?.subID);
      await reloadServices();
    } catch (err) {
      setManageError(err?.message || err?.msg || "Failed to pause subscription.");
    }
  };

  const handleResume = async () => {
    setManageError("");
    try {
      const res = await apiClass.resumeStripeSubscription(manageService?.subID);
      setActionMsg(res?.msg || "Subscription resumed.");
      await loadSubscriptionDetails(manageService?.subID);
      await reloadServices();
    } catch (err) {
      setManageError(err?.message || err?.msg || "Failed to resume subscription.");
    }
  };

  const handleCancel = async () => {
    setManageError("");
    try {
      const res = await apiClass.cancelStripeSubscription(manageService?.subID);
      setActionMsg(res?.msg || "Subscription will cancel at period end.");
      await loadSubscriptionDetails(manageService?.subID);
      await reloadServices();
    } catch (err) {
      setManageError(err?.message || err?.msg || "Failed to cancel subscription.");
    }
  };

  const handleConnect = (_item) => {
    setActionMsg("Connect link is not configured yet.");
  };

  const token = typeof window !== "undefined" && localStorage.getItem("hosting-noodle-token");
  if (!token) {
    return null;
  }

  return (
    <>
      <Navbar />
      <main className="hn-dashboard-main">
        <Container className="py-4 py-md-5">
          <h1 className="hn-dashboard-title">Dashboard</h1>

          <section className="hn-dashboard-section">
            <h2 className="hn-dashboard-section-title">Services</h2>
            <p className="text-muted small mb-3">
              Your Hosting Noodle products. Manage your subscription from here.
            </p>
            {loading && (
              <div className="text-center py-4">
                <Spinner color="warning" />
              </div>
            )}
            {error && (
              <Alert color="danger">{error}</Alert>
            )}
            {actionMsg && (
              <Alert color="success">{actionMsg}</Alert>
            )}
            {!loading && !error && services.length === 0 && (
              <p className="text-muted">You have no Hosting Noodle services yet. Purchase from the <a href="/#hn-plans">Plans</a> section.</p>
            )}
            {!loading && services.length > 0 && (
              <Row className="g-3">
                {services.map((item, i) => (
                  <Col key={i} xs="12" md="6" lg="4">
                    <Card className="hn-service-card">
                      <CardBody>
                        <h3 className="hn-service-name">{item.displayName}</h3>
                        {item.licenseKey && (
                          <p className="small mb-1">
                            <span className="text-muted">RTC key: </span>
                            <span style={{ fontFamily: "monospace" }}>{item.licenseKey}</span>
                          </p>
                        )}
                        <p className="hn-service-expiry small text-muted mb-1">
                          Expires: {item.expiresOn}
                        </p>
                        {item.daysLeft !== undefined && (
                          <p className="hn-service-days small mb-2">
                            {item.daysLeft > 0 ? `${item.daysLeft} days left` : "Expired"}
                          </p>
                        )}
                        {CONNECTABLE_PIDS.has(parseInt(item.productID, 10)) && (
                          <Button
                            color="warning"
                            outline
                            size="sm"
                            className="me-2"
                            onClick={() => handleConnect(item)}
                            disabled
                          >
                            Connect to instance
                          </Button>
                        )}
                        <Button
                          color="secondary"
                          outline
                          size="sm"
                          onClick={() => openManage(item)}
                        >
                          Manage subscription
                        </Button>
                      </CardBody>
                    </Card>
                  </Col>
                ))}
              </Row>
            )}
          </section>
        </Container>
      </main>
      <Footer />

      <Modal isOpen={manageOpen} toggle={closeManage} centered>
        <ModalHeader toggle={closeManage}>Manage subscription</ModalHeader>
        <ModalBody>
          {manageService?.displayName && (
            <p className="mb-2">
              <strong>{manageService.displayName}</strong>
            </p>
          )}
          {manageLoading && <p className="text-muted mb-0">Loading subscription…</p>}
          {manageError && <Alert color="danger" className="mt-2 mb-0">{manageError}</Alert>}
          {!manageLoading && !manageError && subDetails && (
            <>
              <p className="mb-2">
                <span className="text-muted">Sub ID: </span>
                <span style={{ fontFamily: "monospace" }}>{subDetails.subID}</span>
              </p>
              <p className="mb-2">
                <span className="text-muted">Status: </span>
                {(() => {
                  const isCancelled = Boolean(subDetails.cancelAtPeriodEnd) || String(subDetails.status) === "canceled";
                  const isPaused = Boolean(subDetails.paused) || String(subDetails.status) === "paused";
                  const label = isCancelled ? "cancelled" : isPaused ? "paused" : "active";
                  const color = label === "active" ? "success" : label === "paused" ? "secondary" : "danger";
                  return <Badge color={color}>{label}</Badge>;
                })()}
              </p>
            </>
          )}
          {!manageLoading && !manageError && !subDetails && (
            <p className="text-muted mb-0">No subscription found.</p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={handlePause} disabled={manageLoading}>
            Pause
          </Button>
          <Button color="secondary" outline onClick={handleResume} disabled={manageLoading}>
            Resume
          </Button>
          <Button color="danger" onClick={handleCancel} disabled={manageLoading}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default DashboardPage;
